/**
 * /market/:slug/review — NPS postventa + CTA Google Reviews (Epic 2/3).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, ExternalLink, Loader2, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackReviewLinkClicked } from "@/lib/storefront-funnel";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";

export const Route = createFileRoute("/market_/$slug/review")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  validateSearch: (s: Record<string, unknown>) => ({
    order: typeof s.order === "string" ? s.order : undefined,
  }),
  component: ReviewPage,
  head: ({ params }) => ({ meta: [{ title: `Tu opinión — ${params.slug}` }] }),
});

type ReviewConfig = {
  tenantName: string;
  slug: string;
  googleReviewUrl: string | null;
  npsPrompt: string;
  primaryColor: string | null;
  logoUrl: string | null;
};

function ReviewPage() {
  const { slug } = Route.useParams();
  const { order } = Route.useSearch();
  const [cfg, setCfg] = useState<ReviewConfig | null>(null);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [state, setState] = useState<"loading" | "ready" | "sending" | "done" | "error">("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    void fetch(`/api/market/${encodeURIComponent(slug)}/review`)
      .then(async (r) => {
        if (!r.ok) throw new Error("Tienda no encontrada");
        return (await r.json()) as ReviewConfig;
      })
      .then((data) => { setCfg(data); setState("ready"); })
      .catch((e) => { setError(e instanceof Error ? e.message : "Error"); setState("error"); });
  }, [slug]);

  const onGoogleClick = (source: string) => {
    trackReviewLinkClicked(slug, {
      orderId: order,
      source,
      channel: "storefront",
    });
  };

  const submit = async () => {
    if (score == null) return;
    setState("sending");
    try {
      const r = await fetch(`/api/market/${encodeURIComponent(slug)}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ score, comment: comment.trim() || undefined, orderId: order || undefined }),
      });
      const data = (await r.json()) as { error?: string };
      if (!r.ok) throw new Error(data.error ?? "No se pudo enviar");
      setState("done");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error");
      setState("error");
    }
  };

  const showGoogleCta = Boolean(cfg?.googleReviewUrl) && score != null && score >= 8;
  const accent = cfg?.primaryColor ?? "#16a34a";

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#f8fafc" }}>
      <div
        className="max-w-md w-full rounded-2xl border border-border/60 bg-white p-6 space-y-5 shadow-sm"
        data-testid="review-page"
      >
        {state === "loading" ? (
          <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : state === "done" ? (
          <div className="space-y-4" data-testid="review-thank-you">
            <CheckCircle2 className="h-12 w-12 mx-auto" style={{ color: accent }} />
            <h1 className="text-xl font-semibold text-center text-gray-900">¡Gracias por tu opinión!</h1>
            <p className="text-sm text-gray-500 text-center">
              Tu feedback ayuda a {cfg?.tenantName ?? "nosotros"} a mejorar.
            </p>
            {showGoogleCta ? (
              <div className="space-y-2 rounded-xl border border-gray-100 bg-gray-50 p-4">
                <p className="text-sm font-medium text-center text-gray-800">
                  ¿Nos dejás también una reseña pública en Google?
                </p>
                <p className="text-[11px] text-center text-gray-400">
                  Ayuda a otros clientes a encontrarnos. Toma menos de un minuto.
                </p>
                <a
                  href={cfg!.googleReviewUrl!}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onGoogleClick("review_thank_you")}
                  className="flex items-center justify-center gap-2 w-full rounded-lg py-3 text-sm font-semibold"
                  style={{ background: accent, color: "#fff" }}
                  data-testid="google-review-cta"
                >
                  Dejanos tu reseña <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            ) : cfg?.googleReviewUrl ? (
              <a
                href={cfg.googleReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onGoogleClick("review_secondary")}
                className="flex items-center justify-center gap-1.5 text-xs hover:underline"
                style={{ color: accent }}
                data-testid="google-review-secondary"
              >
                Dejanos tu reseña en Google <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              <p className="text-[11px] text-center text-gray-400" data-testid="google-review-missing">
                El comercio aún no configuró un link de Google Reviews.
              </p>
            )}
            <Link
              to="/market/$slug"
              params={{ slug }}
              className="flex items-center justify-center w-full rounded-lg py-3 text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Volver a la tienda
            </Link>
          </div>
        ) : state === "error" && !cfg ? (
          <>
            <h1 className="text-xl font-semibold text-center">No disponible</h1>
            <p className="text-sm text-muted-foreground text-center">{error}</p>
          </>
        ) : (
          <>
            <div className="text-center space-y-2">
              {cfg?.logoUrl ? (
                <img src={cfg.logoUrl} alt={cfg.tenantName} className="h-12 w-auto mx-auto object-contain" />
              ) : (
                <Star className="h-8 w-8 text-amber-500 mx-auto" />
              )}
              <h1 className="text-xl font-semibold text-gray-900">{cfg?.tenantName ?? "Tu experiencia"}</h1>
              <p className="text-sm text-gray-500">{cfg?.npsPrompt}</p>
            </div>
            <div className="space-y-2">
              <div
                className="grid grid-cols-11 gap-1"
                role="radiogroup"
                aria-label="Qué tan probable es que nos recomiendes"
              >
                {Array.from({ length: 11 }, (_, i) => (
                  <button
                    key={i}
                    type="button"
                    role="radio"
                    aria-checked={score === i}
                    onClick={() => setScore(i)}
                    className={cn(
                      "aspect-square w-full min-w-0 rounded-md border text-xs font-semibold tabular-nums transition-colors sm:text-sm",
                      score !== i && "border-gray-200 hover:bg-gray-50 text-gray-700",
                    )}
                    style={score === i ? { background: accent, borderColor: accent, color: "#fff" } : undefined}
                  >
                    {i}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-[11px] text-gray-400 px-0.5">
                <span>Nada probable</span>
                <span>Muy probable</span>
              </div>
            </div>
            <Textarea placeholder="Comentario opcional" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="border-gray-200 text-gray-900 placeholder:text-gray-400" />
            {state === "error" && error ? <p className="text-sm text-red-500 text-center">{error}</p> : null}
            <button
              type="button"
              className="w-full rounded-lg py-3 text-sm font-semibold transition-opacity disabled:opacity-50"
              style={{ background: accent, color: "#fff" }}
              disabled={score == null || state === "sending"}
              onClick={() => void submit()}
            >
              {state === "sending" ? "Enviando…" : "Enviar opinión"}
            </button>
            {cfg?.googleReviewUrl ? (
              <a
                href={cfg.googleReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onGoogleClick("review_form")}
                className="flex items-center justify-center gap-1.5 text-xs text-primary hover:underline"
                data-testid="google-review-pre"
              >
                Preferís dejar reseña en Google <ExternalLink className="h-3 w-3" />
              </a>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
